import {
  Box,
  Container,
  Stack,
  CircularProgress,
  Alert,
  AlertTitle,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import axios from "axios";
import React from "react";
import ProductPricingTable from "./ProductPricingTable";
import LoadingButton from "@mui/lab/LoadingButton";
import { getAuth } from "firebase/auth";
import { useParams, useSearchParams } from "react-router-dom";
import StoreSelector from "./StoreSelector";
import { SERVICE_URL } from "./App";
import { getProducts } from "./managers/ProductManager";
import TaskList from "./InksoftSyncoreImport/TaskList";

function PricingPage(props) {
  let { db } = props;
  let { domainId } = useParams();
  let [searchParams, setSearchParams] = useSearchParams();
  let [products, setProducts] = React.useState([]);
  let [originalProducts, setOriginalProducts] = React.useState([]);
  let [submitting, setSubmitting] = React.useState(false);
  let [enableSizeGroups] = React.useState(false);
  let [error, setError] = React.useState(null);
  let [productError, setProductError] = React.useState(null);
  let [globalMargin, setGlobalMargin] = React.useState("");

  const getData = React.useCallback(async () => {
    try {
      setProductError(null);
      let products = await getProducts(
        domainId,
        searchParams.get("inksoftStoreURL"),
        true,
        false,
        true,
        true
      );
      setProducts(products);
      setOriginalProducts(JSON.parse(JSON.stringify(products)));
    } catch (error) {
      setProductError(error.toString());
    }
  }, [domainId, searchParams]);

  React.useEffect(() => {
    if (searchParams.get("inksoftStoreURL") !== null) getData();
  }, [searchParams, getData]);

  const handleStoreChange = (storeUrl) => {
    let newParams = new URLSearchParams(searchParams.toString());
    newParams.set("inksoftStoreURL", storeUrl);
    setSearchParams(newParams);
    setProducts([]);
    // this.setState({inksoftUrl: event.target.value, products: []}, () => this.getData());
  };

  const getAdditionalCosts = (cost, additionalCosts) => {
    let total = 0;
    for (const costConfig of additionalCosts || []) {
      if (costConfig.mode === "ProductCostPercentage") {
        total += (cost * costConfig.percentage) / 100;
      } else {
        total += costConfig.cost ?? 0;
      }
    }
    return Math.round(total * 100) / 100;
  };

  const getCommissionFromBasePrice = (basePrice, commissionData) => {
    if (commissionData?.mode === "PricePercentage") {
      return (
        Math.round(
          ((basePrice * 100) / (100 - commissionData.percentage)) * 100 -
            basePrice * 100
        ) / 100
      );
    } else {
      return commissionData?.commission ?? 0;
    }
  };

  const round = (num, decimals) => {
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  };

  const applyMargin = () => {
    if (!globalMargin) return;
    let marginNum = parseFloat(globalMargin);
    let newProducts = JSON.parse(JSON.stringify(products));
    for (let product of newProducts) {
      for (let style of product.styles) {
        for (let size of style.sizes) {
          size.margin = marginNum.toString();
          let marginPercent = marginNum / 100;
          let blankCost = size.supplierCost;
          if (size.decorationCostIncluded) {
            blankCost += size.decoCost;
          }
          blankCost += getAdditionalCosts(
            size.supplierCost,
            size.additionalCosts
          );
          let basePrice = (blankCost + size.decoCost) / (1 - marginPercent);
          let commission = getCommissionFromBasePrice(
            basePrice,
            size.commissionData
          );
          size.price = round(basePrice + commission, 2);
        }
      }
    }
    setProducts(newProducts);
  };

  const submit = async () => {
    setSubmitting(true);
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    let submitProducts = [];
    let skuSet = new Set();
    for (let pIdx = 0; pIdx < products.length; pIdx++) {
      let product = products[pIdx];
      let originalProduct = originalProducts[pIdx];
      for (let cIdx = 0; cIdx < product.styles.length; cIdx++) {
        let color = product.styles[cIdx];
        let originalColor = originalProduct.styles[cIdx];
        for (let sIdx = 0; sIdx < color.sizes.length; sIdx++) {
          let size = color.sizes[sIdx];
          let originalSize = originalColor.sizes[sIdx];
          if (
            size.supplierCost !== originalSize.supplierCost ||
            size.price !== originalSize.price
          ) {
            if (!skuSet.has(product["sku"])) {
              submitProducts.push(product);
              skuSet.add(product["sku"]);
            }
          }
        }
      }
    }
    try {
      if (submitProducts.length > 0) {
        await axios.post(
          `${SERVICE_URL}/inksoft/setProducts`,
          {
            products: submitProducts,
          },
          {
            params: {
              domainId: domainId,
              updateInventory: "false",
              updatePricing: "true",
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      setSubmitting(false);
      setProducts([]);
      setError(null);
      await getData();
    } catch (e) {
      console.log(e.response.data.message);
      let error = e.response.data.message ?? "Fatal error";
      setSubmitting(false);
      setError(error);
    }
  };

  return (
    <Container>
      <Stack mt={1} direction="column" alignItems="center">
        <TaskList db={db} types={["updateInksoftProductFull"]} />
        <Alert style={{ marginTop: "8px" }} severity="warning">
          <AlertTitle>Warning</AlertTitle>
          Once changes are saved, an asynchronous task is created for each
          product updated. You can view the progress above. Changes will not be
          reflected until these tasks are completed.
        </Alert>
        <StoreSelector
          value={searchParams.get("inksoftStoreURL") ?? ""}
          handleStoreChange={handleStoreChange}
        />
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Global Margin Adjustment
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" mt={2}>
              <TextField
                label="Global Margin (%)"
                type="number"
                value={globalMargin}
                onChange={(e) => setGlobalMargin(e.target.value)}
                sx={{ width: 200 }}
              />
              <Button variant="contained" onClick={applyMargin}>
                Apply Margin to All
              </Button>
            </Stack>
          </CardContent>
        </Card>
        {products.length === 0 &&
          searchParams.get("inksoftStoreURL") !== null &&
          productError === null && (
            <Box
              sx={{
                marginLeft: "auto",
                marginRight: "auto",
                width: 100,
                height: "40vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}
        {productError !== null && (
          <Alert sx={{ margin: "16px" }} severity="error">
            {productError}
          </Alert>
        )}
        {products.map((p, idx) => (
          <ProductPricingTable
            key={p.id.toString() + enableSizeGroups.toString()}
            enableSizeGroups={enableSizeGroups}
            index={idx}
            product={p}
            originalProduct={originalProducts[idx]}
            onValueChanged={() => {}}
          ></ProductPricingTable>
        ))}
      </Stack>
      {error !== null && (
        <Alert sx={{ margin: "16px" }} severity="error">
          {error}
        </Alert>
      )}
      {products.length !== 0 && (
        <Box
          padding={2}
          paddingBottom={4}
          display="flex"
          justifyContent="center"
        >
          <LoadingButton
            loading={submitting}
            variant="contained"
            onClick={submit}
          >
            Update Products
          </LoadingButton>
        </Box>
      )}
    </Container>
  );
}

export default PricingPage;
